import { Box, styled, Typography } from '@mui/material';

import { RoleStageStatus, StepStatusTone } from './application-progress';

export const MUTED = '#808080';
const ACCENT = '#0368A3';
const ACCENT_SURFACE = '#EDF6FB';
const BULLET_BORDER = '#D5D8DC';

export const BULLET_SIZE = 19;

export const StepsList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

export const StepRow = styled(Box, {
  shouldForwardProp: prop => prop !== '$bullet',
})<{ $bullet: boolean }>(({ theme, $bullet }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: $bullet ? theme.spacing(1, 0) : theme.spacing(2, 0),
  borderBottom: $bullet ? 'none' : `1px solid ${theme.palette.main.grey200}`,

  '&:first-of-type': { paddingTop: 0 },
  '&:last-of-type': { borderBottom: 'none', paddingBottom: 0 },

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

export const StepMain = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.75),
  minWidth: 0,
  flex: 1,
}));

export const StepIconWrap = styled(Box)({
  display: 'flex',
  flexShrink: 0,
  marginTop: 2,
});

export const StepBullet = styled('span', {
  shouldForwardProp: prop => prop !== '$current',
})<{ $current: boolean }>(({ $current }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: BULLET_SIZE,
  height: BULLET_SIZE,
  borderRadius: '50%',
  border: `1px solid ${$current ? ACCENT : BULLET_BORDER}`,
}));

export const StepBulletDot = styled('span')({
  width: 7,
  height: 7,
  borderRadius: '50%',
  backgroundColor: ACCENT,
});

export const ListBullet = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: BULLET_SIZE,
  height: BULLET_SIZE,
  flexShrink: 0,

  '&::before': {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.palette.main.black,
  },
}));

export const StepBody = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
  flex: 1,
});

export const StepTitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
}));

export const StepTitle = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 500,
  lineHeight: '24px',
  color: theme.palette.main.black,
}));

export const StepText = styled(Typography)({
  fontSize: 13,
  lineHeight: 1.6,
  color: MUTED,
  whiteSpace: 'pre-line',
  overflowWrap: 'anywhere',
});

export const StepAside = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0,
  marginTop: 2,

  [theme.breakpoints.down('sm')]: {
    marginLeft: BULLET_SIZE + 14,
  },
}));

const statusColor = (
  tone: StepStatusTone,
  errorColor: string,
  mutedColor: string
): string => {
  switch (tone) {
    case StepStatusTone.Accent:
      return ACCENT;
    case StepStatusTone.Error:
      return errorColor;
    default:
      return mutedColor;
  }
};

export const StepStatusLabel = styled('span', {
  shouldForwardProp: prop => prop !== '$tone',
})<{ $tone: StepStatusTone }>(({ theme, $tone }) => ({
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 400,
  color: statusColor(
    $tone,
    theme.palette.error.main,
    theme.palette.main.grey600
  ),
}));

export const RoleDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
  marginTop: theme.spacing(1.5),
}));

export const RoleStepper = styled('ol')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  listStyle: 'none',
  padding: 0,
  margin: 0,

  [theme.breakpoints.down('sm')]: {
    gap: 5,
  },
}));

export const RoleStageItem = styled('li')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),

  [theme.breakpoints.down('sm')]: {
    gap: 5,
  },
}));

export const RoleStage = styled('span', {
  shouldForwardProp: prop => prop !== '$status',
})<{ $status: RoleStageStatus }>(({ theme, $status }) => {
  const highlighted = $status !== RoleStageStatus.Upcoming;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 10px',
    borderRadius: 20,
    border: `1px solid ${highlighted ? ACCENT : BULLET_BORDER}`,
    backgroundColor: highlighted ? ACCENT_SURFACE : 'transparent',
    color: highlighted ? theme.palette.main.black : MUTED,
    fontSize: 12,
    lineHeight: '18px',
    whiteSpace: 'nowrap',

    '& svg': { flexShrink: 0 },

    [theme.breakpoints.down('sm')]: {
      padding: '4px 7px',
      gap: 4,
      fontSize: 11,
    },
  };
});

export const RoleStageConnector = styled('span')({
  display: 'inline-flex',
  flexShrink: 0,
});

export const RoleActionRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
}));

export const RoleActionCopy = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  flex: 1,
  minWidth: 0,
});

export const RoleActionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
  color: theme.palette.main.black,
}));

export const RoleActionStatus = styled('span', {
  shouldForwardProp: prop => prop !== '$tone',
})<{ $tone: StepStatusTone }>(({ theme, $tone }) => ({
  fontSize: 12,
  lineHeight: '18px',
  fontWeight: 500,
  color: statusColor(
    $tone,
    theme.palette.error.main,
    theme.palette.main.grey600
  ),
}));

export const ErrorText = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  lineHeight: '20px',
  color: theme.palette.error.main,
}));

export const WithdrawNote = styled(Typography)({
  fontSize: 13,
  lineHeight: '20px',
  fontWeight: 400,
  color: MUTED,
});

export const WithdrawLink = styled('button')(({ theme }) => ({
  display: 'inline',
  padding: 0,
  border: 'none',
  background: 'transparent',
  font: 'inherit',
  verticalAlign: 'baseline',
  color: theme.palette.main.black,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  cursor: 'pointer',
}));

export const BannerActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2, 3),
}));

export const ProjectInfoLink = styled('button')(({ theme }) => ({
  padding: 0,
  border: 'none',
  background: 'transparent',
  fontFamily: 'inherit',
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 500,
  color: theme.palette.main.black,
  textDecoration: 'none',
  textUnderlineOffset: 4,
  cursor: 'pointer',

  '&:hover, &:focus-visible': {
    textDecoration: 'underline',
  },
}));

export const HeaderBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5, 3),
  marginBottom: theme.spacing(3),
}));

export const HeaderBackButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  padding: 0,
  border: 'none',
  background: 'transparent',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 400,
  color: MUTED,
  cursor: 'pointer',

  '&:hover': { color: theme.palette.main.black },
}));

export const HeaderActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5, 2.5),
  marginLeft: 'auto',
  minWidth: 0,
}));

export const SummaryTrigger = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: 0,
  border: 'none',
  background: 'transparent',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 400,
  color: theme.palette.main.black,
  cursor: 'pointer',

  '& svg': { color: MUTED },
  '&:hover, &:focus-visible': { textDecoration: 'underline' },
}));

export const OutcomeFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2.5),
}));

export const SavedNote = styled(Typography)({
  fontSize: 13,
  lineHeight: '20px',
  color: MUTED,
});

export const RoleActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  flexShrink: 0,
}));
